/**
 * Client-side router using history-based (path) URLs
 * Switched from hash URLs to clean paths for SEO/AdSense crawlability.
 */

type RouteChangeCallback = (path: string) => void;

class Router {
  private listeners: RouteChangeCallback[] = [];
  private initialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      // Initialize routing
      this.setupListeners();
    }
  }

  private setupListeners(): void {
    if (this.initialized) return;

    // History navigation
    window.addEventListener('popstate', () => this.notifyListeners());

    this.initialized = true;
  }

  /**
   * Navigate to a new route without page reload
   */
  navigate(path: string): void {
    if (typeof window === 'undefined') return;

    const formattedPath = normalizePath(path);

    try {
      window.history.pushState(null, '', formattedPath);
      // pushState does not trigger popstate; notify manually
      this.notifyListeners();
    } catch (e) {
      // Fallback
      window.location.assign(formattedPath);
    }
  }

  /**
   * Get current route path
   */
  getCurrentPath(): string {
    if (typeof window === 'undefined') return '/';
    const { pathname, search } = window.location;
    const p = pathname || '/';
    return search ? `${p}${search}` : p;
  }

  /**
   * Subscribe to route changes
   */
  subscribe(callback: RouteChangeCallback): () => void {
    this.listeners.push(callback);
    
    // Setup listeners if not already done
    this.setupListeners();
    
    // Immediate callback with current path
    callback(this.getCurrentPath());
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of route change
   */
  private notifyListeners(): void {
    const currentPath = this.getCurrentPath();
    this.listeners.forEach(callback => callback(currentPath));
  }
}

// Create a singleton router instance
export const router = new Router();

// Hook for functional components to use router
export function useRouter() {
  return {
    navigate: (path: string) => router.navigate(path),
    currentPath: router.getCurrentPath(),
  };
}

/**
 * Normalize a navigation target to a clean path:
 * - strips hash prefix if present
 * - ensures leading '/'
 * - preserves query string
 */
export function normalizePath(input: string): string {
  if (!input) return '/';
  let s = input.trim();
  // Strip leading '#' if provided
  if (s.startsWith('#')) s = s.slice(1);
  // If includes protocol/host, keep pathname+search only
  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      s = u.pathname + (u.search || '');
    }
  } catch {}
  // Ensure starts with '/'
  if (!s.startsWith('/')) s = `/${s}`;
  return s;
}




